<template>
<Layout title="Task Manager" description="Easily create, edit, and track your tasks in one place. Stay on top of deadlines, monitor progress, and prioritize what matters most with a clear and organized workflow.">
  <div class="p-6 space-y-6">
    <!-- Header -->
    <div class="flex justify-end">
      <button
        class="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 text-sm"
        @click="openDialog()"
      >
        <Plus class="h-4 w-4" />
        <span class="hidden sm:inline">Add Task</span>
      </button>

    </div>

    <!-- Dialog -->
    <div
      v-if="isDialogOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <h2 class="text-xl font-semibold mb-4">
          {{ editingTask ? "Edit Task" : "Create New Task" }}
        </h2>
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              v-model="formData.title"
              required
              class="w-full border rounded px-3 py-2"
              placeholder="Enter task title"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Description</label>
            <textarea
              v-model="formData.description"
              rows="3"
              class="w-full border rounded px-3 py-2"
              placeholder="Enter task description"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">End Date</label>
            <input
              type="date"
              v-model="formData.endDate"
              required
              class="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Customer</label>
            <select
            v-model="formData.CustomerID"
            required
            class="w-full border rounded px-3 py-2"
            >
            <option disabled value="">Select a customer</option>
            <option
                v-for="c in customerList"
                :key="c.id"
                :value="c.id"
            >
                {{ c.name }}
            </option>
            </select>
        </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Priority</label>
              <select v-model="formData.priority" class="w-full border rounded px-3 py-2">
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
                <label class="block text-sm font-medium mb-1">Status</label>
                    <select v-model="formData.status" class="w-full border rounded px-3 py-2">
                        <option value="todo">Todo</option>
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="waiting_on_client">Waiting on Client</option>
                        <option value="blocked">Blocked</option>
                        <option value="completed">Completed</option>
                    </select>
                </div>
          </div>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded border"
              @click="isDialogOpen = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded bg-blue-600 text-white"
            >
              {{ editingTask ? "Update Task" : "Create Task" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Task Table -->
    <div class="border rounded-lg shadow-sm overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead class="bg-gray-50">
          <tr>
            <th class="p-3 border-b">Title</th>
            <th class="p-3 border-b">Description</th>
            <th class="p-3 border-b">Customer</th>
            <th class="p-3 border-b">End Date</th>
            <th class="p-3 border-b">Priority</th>
            <th class="p-3 border-b">Status</th>
            <th class="p-3 border-b">Created</th>
            <th class="p-3 border-b text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="task in tasks" :key="task.id" class="hover:bg-gray-50">
            <td class="p-3 font-medium">{{ task.title }}</td>
            <td class="p-3 max-w-[300px] break-words whitespace-pre-wrap">
              {{ task.description }}
            </td>
            <td class="p-3">
                <!-- show Customer ID, or resolve to name if you got customerList -->
                {{ getCustomerName(task.CustomerID) }}
            </td>
            <td class="p-3">{{ formatDate(task.endDate) }}</td>
            <td class="p-3">
              <span
                class="px-2 py-1 text-xs rounded-full"
                :class="getPriorityColor(task.priority)"
              >
                {{ task.priority }}
              </span>
            </td>
            <td class="p-3">
              <span
                class="px-2 py-1 text-xs rounded-full"
                :class="getStatusColor(task.status)"
              >
                {{ task.status }}
              </span>
            </td>
            <td class="p-3">{{ formatDate(task.createdAt) }}</td>
            <td class="p-3 text-right">
              <button
                class="p-1 text-gray-600 hover:text-blue-600"
                @click="handleEdit(task)"
              >
                <Edit class="h-4 w-4" />
              </button>
              <button
                class="p-1 text-gray-600 hover:text-red-600"
                @click="deleteTask(task.id)"
              >
                <Trash2 class="h-4 w-4" />
              </button>
            </td>
          </tr>
          <tr v-if="tasks.length === 0">
            <td colspan="7" class="text-center py-8 text-gray-400">
              No tasks found. Create your first task to get started.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  </Layout>
</template>

<script>
import { Plus, Edit, Trash2 } from "lucide-vue-next";
import Layout from '@/components/Layout.vue';
import { useToast } from 'vue-toastification'
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import axios from "axios";

export default {
  name: "Tasks",
  components: {Layout,  Plus, Edit, Trash2 },
  data() {
    return {
      tasks: [
      ],
      editingTask: null,
      isDialogOpen: false,
      formData: {
        title: "",
        description: "",
        endDate: "",
        priority: "Medium",
        status: "todo",
      },
      userID:"",
      // apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app",
      // apiURL: "http://127.0.0.1:5001",
      apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app", 
      customerList:[]

    };
  },
    setup() {
    const toast = useToast()

    return {
      toast
    }
  },
  async mounted(){
      this.userID = this.$store.getters.getUserId;
      await this.checkAuthState();
      await this.loadCustomers();
      await this.fetchActiveTasks();

  },
  methods: {

    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');

      const token = await user.getIdToken(); // 🔐 always fresh
      return {
        Authorization: token
      };
    },
    async checkAuthState() {
        const auth = getAuth();
        return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
            if (user) {
            try {
                const idToken = await user.getIdToken();
                this.token = idToken;
                resolve(); // 🔥 Only resolve when token is ready
            } catch (error) {
                reject(error);
            }
            } else {
            router.push("/");
            reject(new Error("No user logged in"));
            }
        });
        });
    },
    getCustomerName(id) {
    const customer = this.customerList.find(c => c.id === id);
    return customer ? customer.name : id; // fallback to ID if name not found
    },
    async loadCustomers() {
        try {
          const headers = await this.getAuthHeader();

          const res = await axios.get(`${this.apiURL}/getAllCompanies`, {
            params: { userID: this.userID },
            headers
          });

          this.customerList = res.data.data;
        } catch (err) {
          this.toast.error("Error Fetching")
          // console.error("❌ Failed to load customers:", err.message);
        }
      },
    async createTask(payload) {
      try {
        const headers = await this.getAuthHeader();
        await axios.post(`${this.apiURL}/api/tasks`, payload, {
          headers
        });
        this.toast.success("Task Created")
        await this.fetchActiveTasks();
      } catch (err) {
        this.toast.error("Create task failed");
      }
    },

    // Update task
    async updateTask(taskId, payload) {
      try {
        const headers = await this.getAuthHeader();
        await axios.put(`${this.apiURL}/api/tasks/${taskId}`, payload, {
          headers
        });
        this.toast.success("Task Updated")
        await this.fetchActiveTasks();
      } catch {
        this.toast.error("Update task failed");
      }
    },

    // Delete task
    async deleteTask(taskId) {
      try {
        const headers = await this.getAuthHeader();
        await axios.delete(`${this.apiURL}/api/tasks/${taskId}`, {
          headers
        });
        this.toast.success("Task Deleted")
        await this.fetchActiveTasks();
      } catch {
        this.toast.error("Delete task failed");
      }
    },

    // Get active tasks
    async fetchActiveTasks() {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.apiURL}/api/tasks-active`, {
          headers,
        });
        this.tasks = res.data; // array of tasks
      } catch (err) {
        this.toast.error("Fetch active tasks failed");
      }
    },
    openDialog() {
      this.resetForm();
      this.isDialogOpen = true;
    },
    resetForm() {
      this.formData = {
        title: "",
        description: "",
        endDate: "",
        priority: "Medium",
        status: "todo",
      };
      this.editingTask = null;
    },
    async handleSubmit() {
        try {
        if (this.editingTask) {
            // 🔄 Update existing task
            await this.updateTask(this.editingTask.id, this.formData);
        } else {
            // ➕ Create new task
            await this.createTask(this.formData);
        }

        this.isDialogOpen = false;
        this.resetForm();
        } catch (err) {
        this.toast.error("Failed to save task");
        }
    },
    handleEdit(task) {
        this.editingTask = task;
        this.formData = {
        title: task.title,
        description: task.description,
        endDate: task.endDate,
        priority: task.priority,
        status: task.status,
        CustomerID: task.CustomerID || "", // 🔑 required for backend
        };
        this.isDialogOpen = true;
    },
    handleDelete(id) {
      this.tasks = this.tasks.filter((t) => t.id !== id);
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    getPriorityColor(priority) {
      switch (priority) {
        case "High":
          return "bg-red-100 text-red-800";
        case "Medium":
          return "bg-yellow-100 text-yellow-800";
        case "Low":
          return "bg-green-100 text-green-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
    getStatusColor(status) {
      switch (status) {
        case "Completed":
          return "bg-green-100 text-green-800";
        case "In Progress":
          return "bg-blue-100 text-blue-800";
        case "Pending":
          return "bg-gray-100 text-gray-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    },
  },
};
</script>
